import { useState, useRef } from "react";

/**
 * Wrapper riutilizzabile che aggiunge drag & drop a QUALSIASI area di upload.
 * Non modifica il layout dei children: quando l'utente trascina un file sull'area
 * chiama onFile(file). Fornisce anche feedback visivo (bordo blu) durante il drag.
 *
 * Props:
 * - onFile(file): callback chiamato quando un file viene rilasciato
 * - accept?: string mime/estensione consentiti (filtro basic client-side)
 * - disabled?: bool
 * - className?: string classi extra
 * - dropLabel?: string mostrata come overlay durante il drag (default "Rilascia qui il file")
 * - children: JSX (tipicamente pulsante/label upload esistente)
 */
export default function FileDropZone({
  onFile,
  accept,
  disabled = false,
  className = "",
  dropLabel = "Rilascia qui il file",
  children,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const matchesAccept = (file) => {
    if (!accept) return true;
    const tokens = accept.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
    if (!tokens.length) return true;
    const nameLower = (file.name || "").toLowerCase();
    const typeLower = (file.type || "").toLowerCase();
    return tokens.some((tok) => {
      if (tok.startsWith(".")) return nameLower.endsWith(tok);
      if (tok.endsWith("/*")) return typeLower.startsWith(tok.slice(0, -1));
      return typeLower === tok;
    });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    dragCounter.current += 1;
    if (e.dataTransfer?.items?.length) setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!matchesAccept(file)) return;
    onFile?.(file);
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative ${className}`}
      data-dropzone-active={isDragging || undefined}
    >
      {children}
      {isDragging && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-blue-500 bg-blue-500/10 backdrop-blur-[1px]">
          <div className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded shadow">
            {dropLabel}
          </div>
        </div>
      )}
    </div>
  );
}
